import { useEffect, useRef, useState } from "react";
import { checkTokenExpiration } from "../utils/commonUtils";
import { useNavigate } from "react-router-dom";
import useAuth from "../Hooks/useAuth";

/**
 * A unified hook to guard against expired sessions.
 * It handles both:
 *  - Immediate check on mount (for returning users)
 *  - Periodic check in background (for active users)
 *
 * @param expTime - Token expiration timestamp (in seconds)
 * @returns `isChecking` - Whether the session is being validated (used to block rendering)
 */
const useSessionGuard = (expTime: number | null, skipExpiryCheck = false) => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [isChecking, setIsChecking] = useState(true);
    const hasLoggedOutRef = useRef(false);

    useEffect(() => {

        const checkAndHandleLogout = async () => {
            if (skipExpiryCheck) {
                setIsChecking(false);
                return;
            }

            const isExpired = checkTokenExpiration(expTime, 10);

            if (isExpired && !hasLoggedOutRef.current) {
                hasLoggedOutRef.current = true; // prevent multiple triggers
                await logout({ isSessionTimeout: true });
                navigate("/login", { replace: true, state: { sessionExpired: true } });
            } else {
                setIsChecking(false); // done checking once on mount
            }
        };

        // Immediate check
        checkAndHandleLogout();

        // Interval check for active session
        const intervalId = setInterval(checkAndHandleLogout, 5000);

        return () => clearInterval(intervalId);
    }, [expTime, logout, navigate, skipExpiryCheck]);

    return { isChecking };
};

export default useSessionGuard;